import React, { useState } from "react";
import { Plus, X } from "lucide-react";
import { Input, NativeSelect, IconButton, AppButton } from "../ui";
import { SKILL_CATEGORIES, SKILL_LEVELS } from "./constants";

/**
 * SkillsField — the skill list on the career profile.
 *
 * A skill is three facts (name, depth, years), not a tag, because all three feed
 * the resume analysis: "React, beginner, 0 years" and "React, expert, 4 years"
 * ask the grader two different questions about the same word. A plain tag input
 * would have been quicker to build and would have thrown away the half of the
 * signal that makes the report specific.
 *
 * The add row is a form-within-a-form in behaviour but not in markup — this
 * renders inside the career profile's own <form>, so a nested <form> would be
 * invalid HTML and the inner submit would submit the outer one. Enter on the
 * name field adds the skill and is stopped from propagating for the same reason.
 */
const EMPTY = { name: "", level: "intermediate", years: 0, category: "technical" };

const SkillsField = ({ value = [], onChange, disabled = false }) => {
  const [draft, setDraft] = useState(EMPTY);

  const add = () => {
    const name = draft.name.trim();
    if (!name) return;
    // A duplicate is a typo, not an intention — the server de-duplicates too,
    // and silently dropping it here would look like the button did nothing.
    if (value.some((s) => s.name.trim().toLowerCase() === name.toLowerCase())) {
      setDraft(EMPTY);
      return;
    }
    onChange([...value, { ...draft, name }]);
    setDraft(EMPTY);
  };

  const update = (i, patch) => onChange(value.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {value.length > 0 && (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
          {value.map((s, i) => (
            <li
              key={`${s.name}-${i}`}
              className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-p-2 min-[640px]:tw-flex-nowrap"
            >
              <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-px-1 tw-text-sm tw-font-medium tw-text-content">
                {s.name}
              </span>
              <NativeSelect
                size="sm"
                fullWidth={false}
                wrapperClassName="tw-min-w-[8.5rem]"
                aria-label={`Proficiency in ${s.name}`}
                value={s.level}
                disabled={disabled}
                onChange={(e) => update(i, { level: e.target.value })}
              >
                {SKILL_LEVELS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </NativeSelect>
              <Input
                size="sm"
                type="number"
                min={0}
                max={50}
                aria-label={`Years of experience with ${s.name}`}
                value={s.years ?? 0}
                disabled={disabled}
                onChange={(e) => update(i, { years: Number(e.target.value) || 0 })}
                fullWidth={false}
                className="tw-w-[4.5rem]"
              />
              <span className="tw-text-xs tw-text-content-subtle">yrs</span>
              <IconButton
                label={`Remove ${s.name}`}
                icon={X}
                variant="ghost-destructive"
                size="sm"
                type="button"
                disabled={disabled}
                onClick={() => remove(i)}
              />
            </li>
          ))}
        </ul>
      )}

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 min-[640px]:tw-flex-nowrap">
        <Input
          size="sm"
          placeholder="Add a skill — React, SQL, Figma…"
          value={draft.name}
          disabled={disabled}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); add(); }
          }}
          aria-label="New skill name"
        />
        <NativeSelect
          size="sm"
          fullWidth={false}
          wrapperClassName="tw-min-w-[9rem]"
          value={draft.category}
          disabled={disabled}
          onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
          aria-label="Skill category"
        >
          {SKILL_CATEGORIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </NativeSelect>
        <AppButton type="button" variant="outline" size="sm" onClick={add} disabled={disabled || !draft.name.trim()}>
          <Plus size={14} /> Add
        </AppButton>
      </div>

      {value.length === 0 && (
        <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
          Your skills tell the resume analysis what to look for. A resume that never mentions a
          skill you have listed is the easiest thing to fix.
        </p>
      )}
    </div>
  );
};

export default SkillsField;
